import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Typography, Form, Spin, Empty } from 'antd';
import { Layout, X, ExternalLink, ArrowRight } from 'lucide-react';
import dialerService from '../services/dialerService';

const { Text } = Typography;

const WebFormModalV2 = ({ isOpen, onClose, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [webForms, setWebForms] = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isOpen && record) {
      fetchWebForms();
    }
  }, [isOpen]);

  const fetchWebForms = async () => {
    setFetching(true);
    try {
      const res = await dialerService.getWebForms();
      if (res) {
        setWebForms(res.map(f => ({ 
          label: f.Text, 
          value: String(f.Id || f.Value || f.Code) 
        })));
      }
    } catch (error) {
      console.error('Failed to fetch webforms:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleOpenForm = (values) => {
    const selectedForm = webForms.find(f => f.value === values.webFormId);
    if (!selectedForm) return;

    // Logic to construct URL similar to legacy jsLEA.addWebForm
    const baseUrl = `${window.location.origin}/UserCRMCampaign/webFormDetails.aspx`;
    const params = new URLSearchParams({
      WebFormID: values.webFormId,
      EnityId: record.Id,
      EntityName: record.EntityName,
      IsModal: '1'
    });

    const url = `${baseUrl}?${params.toString()}`;
    window.open(url, '_blank', 'width=800,height=600');
    onClose();
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Layout size={20} className="text-teal-600" />
          <span className="text-lg font-bold text-slate-800">Fill Webform</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={450}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Spin spinning={fetching}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleOpenForm}
          className="py-4"
        >
          <div className="p-6 bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-100 rounded-[2rem] mb-8 flex flex-col items-center text-center shadow-sm">
            <div className="p-4 bg-white rounded-2xl shadow-md mb-4 group-hover:scale-110 transition-transform">
              <Layout size={32} className="text-teal-500" />
            </div>
            <Text className="text-teal-900 font-extrabold text-xl block mb-1">Select a Form</Text>
            <Text className="text-teal-700/80 text-sm font-medium">Choose the webform you wish to fill for {record?.PersonName || record?.MobileNo || 'this contact'}.</Text>
          </div>

          {webForms.length > 0 ? (
            <Form.Item
              name="webFormId"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> AVAILABLE WEBFORMS</span>}
              rules={[{ required: true, message: 'Please select a form' }]}
            >
              <Select 
                placeholder="Select Webform" 
                options={webForms} 
                className="h-12 premium-select"
                suffixIcon={<ArrowRight size={14} className="text-slate-400" />}
                getPopupContainer={() => document.body}
                virtual={false}
              />
            </Form.Item>
          ) : !fetching && (
            <Empty description="No webforms available for this user" className="py-4" />
          )}

          <div className="flex justify-end gap-3 mt-8 border-t border-slate-100 pt-6">
            <Button onClick={onClose} className="rounded-xl font-bold h-12 px-8 border-slate-200 hover:border-teal-400 hover:text-teal-600 transition-all">
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              disabled={webForms.length === 0}
              icon={<ExternalLink size={18} />}
              className="rounded-xl font-black h-12 px-12 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-lg shadow-teal-200 border-none flex items-center gap-2 group"
            >
              <span>Open Form</span>
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default WebFormModalV2;
